import { useState } from "react";
import {
  ThumbsUp,
  Check,
  FileSignature,
  FolderLock,
  MessagesSquare,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { PageShell } from "@/components/interior-page";
import { useToast } from "@/components/ui/toast";

/* Upcoming Modules — the product roadmap.

   Funding Marketplace graduated off this list: it ships at /buyer/funding
   (pages/FundingMarketplace.tsx). Delete the card here when a module launches,
   otherwise the roadmap advertises something the sidebar already links to. Off-market deal browsing moved to
   the "Coming Soon" tab of Available listings on the home page; staff manage
   and publish pipeline leads from the admin Lead Pipeline. */

interface UpcomingModule {
  icon: LucideIcon;
  title: string;
  tagline: string;
  description: string;
  bullets: string[];
  /** Estimated availability shown in the card footer, e.g. "Q4 2026". Keep
   *  these in the future — the hero's rollout stat is derived from them. */
  eta: string;
}

const UPCOMING_MODULES: UpcomingModule[] = [
  {
    icon: FolderLock,
    title: "Data Room (VDR)",
    tagline: "A bank-grade virtual data room on every deal",
    description:
      "Share and review deal documents in a secure virtual data room — permissioned access, watermarking, and a full audit trail of who viewed what.",
    bullets: [
      "Granular, per-party access permissions",
      "Watermarked previews & download control",
      "Complete view/download audit trail",
    ],
    eta: "Q4 2026",
  },
  {
    icon: FileSignature,
    title: "LOI Templates",
    tagline: "From interest to offer in minutes",
    description:
      "Attorney-drafted letter-of-intent templates, pre-filled from the listing and your terms — review, tweak, and send for signature without leaving the platform.",
    bullets: [
      "Attorney-drafted, deal-tested templates",
      "Auto-filled from listing financials & your terms",
      "E-signature ready",
    ],
    eta: "Q4 2026",
  },
  {
    icon: ShieldCheck,
    title: "Compliance Hub",
    tagline: "Every NDA, license, and disclosure in one place",
    description:
      "Track the paperwork that keeps a deal clean — NDAs, licensing requirements, and regulatory checklists organized per deal and per state.",
    bullets: [
      "Deal-level NDA & disclosure tracking",
      "State-by-state licensing checklists",
      "Automatic renewal & expiry reminders",
    ],
    eta: "Q4 2026",
  },
  {
    icon: MessagesSquare,
    title: "Broker Messaging",
    tagline: "Talk to sellers and brokers in-platform",
    description:
      "Keep every conversation attached to the deal — threaded messaging with brokers and sellers, with attachments and read receipts.",
    bullets: [
      "Deal-scoped threaded conversations",
      "Share documents straight from the data room",
      "Read receipts & response-time insights",
    ],
    eta: "Q4 2026",
  },
];

/** Hero rollout stat from the cards' ETAs ("Q4 2026" → value "Q4", label
 *  "Rolling out 2026"), so it can't drift from the cards like a literal did. */
function rolloutStat(modules: UpcomingModule[]): [string, string] {
  const etas = modules
    .map((m) => /^Q([1-4]) (\d{4})$/.exec(m.eta))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => ({ q: Number(m[1]), y: Number(m[2]) }))
    .sort((a, b) => a.y - b.y || a.q - b.q);
  if (etas.length === 0) return ["Soon", "Rolling out"];
  const first = etas[0];
  const last = etas[etas.length - 1];
  if (first.y !== last.y) return [`Q${first.q} ${first.y} – Q${last.q} ${last.y}`, "Rolling out"];
  return [first.q === last.q ? `Q${first.q}` : `Q${first.q}–Q${last.q}`, `Rolling out ${first.y}`];
}

const ComingSoonBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-primary whitespace-nowrap">
    <Sparkles className="h-3 w-3" />
    Coming Soon
  </span>
);

export default function ComingSoonPage() {
  const toast = useToast();
  // Modules the user flagged interest in. Frontend-only — nothing is stored or
  // emailed — so the copy must not promise a launch notification. The button
  // flips to a confirmation so a second click can't double-toast.
  const [notified, setNotified] = useState<Set<string>>(new Set());

  const handleNotify = (title: string) => {
    if (notified.has(title)) return;
    setNotified((prev) => new Set(prev).add(title));
    toast.success(`Thanks — we've noted your interest in ${title}.`);
  };

  return (
    <PageShell>
      {/* Hero — same Dark · Gold treatment as the Home hero: data-theme pins the
          palette to dark for this shell in both themes; the inline transparent
          background keeps the section behind it from painting dark. */}
      <section data-theme="dark" style={{ backgroundColor: "transparent" }} className="px-0">
        <div className="public-hero-shell mx-auto rounded-[1.85rem] px-6 py-8 shadow-glow sm:px-8 sm:py-9">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
          <div className="relative z-10">
            <div className="public-pill">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Product Roadmap
            </div>
            <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl lg:text-5xl">
              Upcoming <span className="text-gradient">Modules</span>
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
              We're building the next generation of tools to take you from first look to closed deal —
              here's a preview of what's landing on BusinessLocating next.
            </p>

            {/* Roadmap stats */}
            <div className="mt-7 flex flex-wrap gap-x-12 gap-y-4">
              {[
                [String(UPCOMING_MODULES.length), "Modules in build"],
                rolloutStat(UPCOMING_MODULES),
              ].map(([value, label]) => (
                <div key={label}>
                  <div className="font-display text-2xl font-bold text-primary sm:text-3xl">{value}</div>
                  <div className="mt-0.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section header */}
      <div className="mt-8 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-xl font-bold tracking-tight text-foreground">On the roadmap</h2>
        <span className="text-xs text-muted-foreground">
          {UPCOMING_MODULES.length} modules in development
        </span>
      </div>

      {/* Module cards */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {UPCOMING_MODULES.map((mod) => {
          const Icon = mod.icon;
          const isNotified = notified.has(mod.title);
          return (
            <div
              key={mod.title}
              className="hover-lift group flex flex-col rounded-2xl border border-border/60 bg-card p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <ComingSoonBadge />
              </div>

              <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-foreground">{mod.title}</h3>
              <p className="mt-1 text-[0.66rem] font-bold uppercase tracking-[0.12em] text-primary/80">
                {mod.tagline}
              </p>
              <p className="mt-2.5 text-[0.8rem] leading-6 text-muted-foreground">{mod.description}</p>

              <ul className="mt-3.5 space-y-1.5">
                {mod.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[0.76rem] leading-5 text-foreground/80">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-primary/70" />
                    {b}
                  </li>
                ))}
              </ul>

              {/* Spacer pins the footer to the card bottom so all footers align */}
              <div className="flex-1" />
              <div className="mt-4 flex items-center justify-between gap-2 border-t border-border/50 pt-3.5">
                <span className="text-[0.7rem] font-medium text-muted-foreground">Est. {mod.eta}</span>
                <button
                  type="button"
                  onClick={() => handleNotify(mod.title)}
                  disabled={isNotified}
                  className={
                    isNotified
                      ? "inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-500"
                      : "inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
                  }
                >
                  {isNotified ? <Check className="h-3.5 w-3.5" /> : <ThumbsUp className="h-3.5 w-3.5" />}
                  {isNotified ? "Interested" : "I'm interested"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </PageShell>
  );
}
